import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ImageOff, Link2, MoreHorizontal, Pencil, Plus, Search, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DataTablePagination } from '@/components/common/data-table-pagination'
import { getErrorMessage } from '@/lib/api'
import { withServiceInstanceId } from '@/lib/service-instance'
import { useStoreOrgScope } from '@/hooks/use-store-org-scope'
import storeCommerceApi from '@/services/store-commerce'
import type { StoreTag, StoreTagPayload } from '@/types'
import { STORE_TAG_ITEM_TYPES } from '@/types'

type TagForm = {
  id?: string | number
  title: string
  code: string
  max_item: string
  item_types: string
  sorting_order: string
  active: string
  logo: File | null
  icon: File | null
  sample_view: File | null
}

const itemTypeOptions: Array<{ value: string; label: string }> = [
  { value: String(STORE_TAG_ITEM_TYPES.product), label: 'Product' },
  { value: String(STORE_TAG_ITEM_TYPES.category), label: 'Category' },
  { value: String(STORE_TAG_ITEM_TYPES.productVariant), label: 'Product Variant' },
  { value: String(STORE_TAG_ITEM_TYPES.offer), label: 'Offer' },
  { value: String(STORE_TAG_ITEM_TYPES.store), label: 'Store' },
]

const TAG_CODE_MAX_LENGTH = 64

const slugifyTagCode = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, TAG_CODE_MAX_LENGTH)

const emptyForm: TagForm = {
  title: '',
  code: '',
  max_item: '100',
  item_types: String(STORE_TAG_ITEM_TYPES.product),
  sorting_order: '',
  active: '1',
  logo: null,
  icon: null,
  sample_view: null,
}

const getScopeLabel = (value?: string | null) => {
  if (value === 'global') return 'Global'
  if (value === 'local') return 'Local'
  return 'Organization'
}

const getScopeBadgeClass = (value?: string | null) => {
  if (value === 'global') return 'bg-blue-100 text-blue-800 border-blue-200'
  if (value === 'local') return 'bg-amber-100 text-amber-800 border-amber-200'
  return 'bg-emerald-100 text-emerald-800 border-emerald-200'
}

const isActive = (value?: number | boolean | null) => !(value === false || value === 0)

const imageCell = (src?: string | null, alt = 'Image') => {
  if (!src) {
    return <div className="flex h-10 w-10 items-center justify-center rounded-md border bg-muted text-muted-foreground"><ImageOff className="h-4 w-4" /></div>
  }

  return <img src={src} alt={alt} className="h-10 w-10 rounded-md border object-cover" />
}

export default function CommerceTagsPage() {
  const { tagsetId } = useParams<{ tagsetId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { serviceInstanceId, orgInstanceId } = useStoreOrgScope()
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState('1')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<StoreTag | null>(null)
  const [linkedItemsTarget, setLinkedItemsTarget] = useState<StoreTag | null>(null)
  const [form, setForm] = useState<TagForm>(emptyForm)

  const tagsetQuery = useQuery({
    queryKey: ['store-tagset', tagsetId],
    queryFn: () => storeCommerceApi.getTagset(tagsetId!),
    enabled: Boolean(tagsetId),
    staleTime: 30_000,
  })

  const tagsQuery = useQuery({
    queryKey: ['store-tags', tagsetId, activeFilter],
    queryFn: () => storeCommerceApi.listTags({
      page: 1,
      page_size: 500,
      tagset: tagsetId,
      active: activeFilter,
    }),
    enabled: Boolean(tagsetId) && Boolean(tagsetQuery.data),
    staleTime: 30_000,
  })

  const tags = tagsQuery.data?.results ?? []

  const linkedItemsQuery = useQuery({
    queryKey: ['store-tag-linked-items', tagsetId, linkedItemsTarget?.id],
    queryFn: () => storeCommerceApi.listTagLinkedItems({
      page: 1,
      page_size: 1,
      tagset: tagsetId,
      tag: linkedItemsTarget!.id,
    }),
    enabled: Boolean(tagsetId) && Boolean(linkedItemsTarget?.id),
    staleTime: 30_000,
  })

  const linkedItems = linkedItemsQuery.data?.results?.[0]
  const linkedProducts = linkedItems?.product_data ?? []
  const linkedCategories = linkedItems?.category_data ?? []
  const filteredTags = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return tags
    return tags.filter((tag) => [tag.title, tag.code, tag.tagset_name, getScopeLabel(tag.scope)].some((value) => String(value ?? '').toLowerCase().includes(term)))
  }, [search, tags])

  const effectivePageSize = Number(pageSize) > 0 ? Number(pageSize) : 10

  const pageRows = useMemo(() => {
    const start = (page - 1) * effectivePageSize
    return filteredTags.slice(start, start + effectivePageSize)
  }, [filteredTags, page, effectivePageSize])

  const generatedCode = slugifyTagCode(form.code || form.title)

  const invalidateTags = () => {
    queryClient.invalidateQueries({ queryKey: ['store-tags'] })
    queryClient.invalidateQueries({ queryKey: ['store-tagsets'] })
  }

  const createMutation = useMutation({
    mutationFn: (payload: StoreTagPayload) => storeCommerceApi.createTag(payload),
    onSuccess: () => {
      invalidateTags()
      toast.success('Tag created')
      setDialogOpen(false)
      setForm(emptyForm)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<StoreTagPayload> }) => storeCommerceApi.updateTag(id, payload),
    onSuccess: () => {
      invalidateTags()
      toast.success('Tag updated')
      setDialogOpen(false)
      setForm(emptyForm)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => storeCommerceApi.deleteTag(id),
    onSuccess: () => {
      invalidateTags()
      toast.success('Tag removed')
      setDeleteTarget(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openCreate = () => {
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEdit = (tag: StoreTag) => {
    setForm({
      id: tag.id,
      title: tag.title ?? '',
      code: tag.code ?? '',
      max_item: String(tag.max_item ?? tag.max_items ?? '100'),
      item_types: String(tag.item_types ?? STORE_TAG_ITEM_TYPES.product),
      sorting_order: String(tag.sorting_order ?? ''),
      active: isActive(tag.active) ? '1' : '0',
      logo: null,
      icon: null,
      sample_view: null,
    })
    setDialogOpen(true)
  }

  const submitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!tagsetId || !form.title.trim()) {
      toast.error('Title is required')
      return
    }

    const payload: StoreTagPayload = {
      tagset: tagsetId,
      title: form.title.trim(),
      code: generatedCode,
      max_item: form.max_item.trim() || '100',
      item_types: Number(form.item_types),
      sorting_order: form.sorting_order.trim() || undefined,
      active: Number(form.active),
      org_instance: orgInstanceId ?? undefined,
      logo: form.logo,
      icon: form.icon,
      sample_view: form.sample_view,
    }

    if (form.id) {
      const editPayload: Partial<StoreTagPayload> = { ...payload }
      delete editPayload.tagset
      delete editPayload.org_instance
      delete editPayload.item_types
      updateMutation.mutate({ id: form.id, payload: editPayload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const backToTagsets = () => navigate(withServiceInstanceId('/services/tagsets', serviceInstanceId))

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="border-b border-border/60">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <Button type="button" variant="ghost" size="icon" onClick={backToTagsets}><ArrowLeft className="h-4 w-4" /></Button>
              <div>
                <CardTitle>Tags</CardTitle>
                <p className="text-sm text-muted-foreground">Tagset: {tagsetQuery.data?.name ?? 'Loading...'}</p>
              </div>
            </div>
            <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="grid gap-3 lg:grid-cols-[1fr_180px]">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search tags" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1) }} />
            </div>
            <Select value={activeFilter} onValueChange={(value) => { setActiveFilter(value); setPage(1) }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="-1">View all</SelectItem>
                <SelectItem value="1">Active</SelectItem>
                <SelectItem value="0">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Max Items</TableHead>
                  <TableHead>Media</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tagsetQuery.isLoading || tagsQuery.isLoading ? (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">Loading tags...</TableCell></TableRow>
                ) : tagsetQuery.isError || tagsQuery.isError ? (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-destructive">{getErrorMessage(tagsetQuery.error ?? tagsQuery.error)}</TableCell></TableRow>
                ) : pageRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No tags found.</TableCell></TableRow>
                ) : pageRows.map((tag) => (
                  <TableRow key={String(tag.id)}>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{tag.title}</span>
                        <Badge variant="outline" className={getScopeBadgeClass(tag.scope)}>{getScopeLabel(tag.scope)}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>{tag.code}</TableCell>
                    <TableCell>{tag.max_item ?? tag.max_items ?? '-'}</TableCell>
                    <TableCell><div className="flex gap-2">{imageCell(tag.logo, 'Logo')}{imageCell(tag.icon, 'Icon')}{imageCell(tag.sample_view, 'Sample')}</div></TableCell>
                    <TableCell><Badge variant={isActive(tag.active) ? 'default' : 'secondary'}>{isActive(tag.active) ? 'Active' : 'Inactive'}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setLinkedItemsTarget(tag)}><Link2 className="mr-2 h-4 w-4" />Linked Items</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(tag)}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(tag)}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination page={page} pageSize={effectivePageSize} totalCount={filteredTags.length} onPageChange={setPage} onPageSizeChange={(value) => { setPageSize(Number(value) || 10); setPage(1) }} />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <form onSubmit={submitForm} className="space-y-4">
            <DialogHeader><DialogTitle>{form.id ? 'Edit Tag' : 'Create Tag'}</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} /></div>
              <div className="space-y-2"><Label>Code</Label><Input value={generatedCode} maxLength={TAG_CODE_MAX_LENGTH} readOnly /><p className="text-xs text-muted-foreground">Auto-generated from title as a slug.</p></div>
              <div className="space-y-2"><Label>Max Items</Label><Input type="number" min="1" value={form.max_item} onChange={(event) => setForm((current) => ({ ...current, max_item: event.target.value }))} /></div>
              <div className="space-y-2"><Label>Item Type</Label><Select value={form.item_types} onValueChange={(value) => setForm((current) => ({ ...current, item_types: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{itemTypeOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Sort Index</Label><Input type="number" min="0" value={form.sorting_order} onChange={(event) => setForm((current) => ({ ...current, sorting_order: event.target.value }))} /></div>
              {form.id ? <div className="space-y-2"><Label>Status</Label><Select value={form.active} onValueChange={(value) => setForm((current) => ({ ...current, active: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">Active</SelectItem><SelectItem value="0">Inactive</SelectItem></SelectContent></Select></div> : null}
              <div className="space-y-2"><Label>Logo</Label><Input type="file" accept="image/*" onChange={(event) => setForm((current) => ({ ...current, logo: event.target.files?.[0] ?? null }))} /></div>
              <div className="space-y-2"><Label>Icon</Label><Input type="file" accept="image/*" onChange={(event) => setForm((current) => ({ ...current, icon: event.target.files?.[0] ?? null }))} /></div>
              <div className="space-y-2 sm:col-span-2"><Label>Sample View</Label><Input type="file" accept="image/*" onChange={(event) => setForm((current) => ({ ...current, sample_view: event.target.files?.[0] ?? null }))} /></div>
            </div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{form.id ? 'Update' : 'Create'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Tag</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Delete {deleteTarget?.title}?</p>
          <DialogFooter><Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button><Button variant="destructive" onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(linkedItemsTarget)} onOpenChange={(open) => !open && setLinkedItemsTarget(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Linked Items: {linkedItemsTarget?.title ?? '-'}</DialogTitle>
          </DialogHeader>
          {linkedItemsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading linked items...</p>
          ) : linkedItemsQuery.isError ? (
            <p className="text-sm text-destructive">{getErrorMessage(linkedItemsQuery.error)}</p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium">Products</p>
                  <Badge variant="outline">{linkedProducts.length}</Badge>
                </div>
                {linkedProducts.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No products linked.</p>
                ) : (
                  <div className="space-y-2">
                    {linkedProducts.map((item) => (
                      <div key={String(item.id)} className="flex items-center gap-2 rounded border px-2 py-1.5">
                        {imageCell(item.logo, 'Product')}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{item.name ?? '-'}</p>
                          {item.description ? <p className="truncate text-xs text-muted-foreground">{item.description}</p> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-md border p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium">Categories</p>
                  <Badge variant="outline">{linkedCategories.length}</Badge>
                </div>
                {linkedCategories.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No categories linked.</p>
                ) : (
                  <div className="space-y-2">
                    {linkedCategories.map((item) => (
                      <div key={String(item.id)} className="flex items-center gap-2 rounded border px-2 py-1.5">
                        {imageCell(item.logo, 'Category')}
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{item.name ?? '-'}</p>
                          {item.description ? <p className="truncate text-xs text-muted-foreground">{item.description}</p> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkedItemsTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
